import { Loader } from '@bitstopco/bitstop-theme';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import CassetteConfigurationCard from '@/components/restocking/CassetteConfigurationCard';
import { DEFAULT_ERROR_MESSAGE } from '@/constants/app/config';
import {
  useEditRestockMutation,
  useGetRestockRequestQuery,
} from '@/store/api/services/restocking/restocking';

import { RestockRequestType } from '@/types/generated/boro';

import type { CassetteView } from '@/types/generated/athena';
import type { UpdateRestockRequest } from '@/types/generated/boro';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';

const EditLoadOrder = ({ props, hide }: ModalChildrenProps) => {
  const id = props?.id;
  const [restockType, setRestockType] = useState<RestockRequestType>(RestockRequestType.Swap);
  const [config, setConfig] = useState<CassetteView[]>([]);
  const [editRestock, { isLoading }] = useEditRestockMutation();
  const [notes, setNotes] = useState('');

  const {
    data: { data: order = null } = {},
    isLoading: isLoadingOrder,
    error,
  } = useGetRestockRequestQuery(id ?? skipToken);

  const isValid = config.length === order?.cassettes?.length;

  const handleChange = (index: number, cassette?: CassetteView) =>
    setConfig((prev) => {
      if (!cassette) {
        return prev.filter((c) => c.cassette_index !== index);
      }
      const withoutCurrent = prev.filter((c) => c.cassette_index !== index);
      const updated = { ...cassette, cassette_index: index };
      return [...withoutCurrent, updated];
    });

  useEffect(() => {
    if (!order) return;
    setRestockType((order.restock_type as RestockRequestType) ?? RestockRequestType.Swap);
    setNotes(order.notes ?? '');
  }, [order]);

  const handleUpdate = async () => {
    try {
      const data: UpdateRestockRequest = {
        restock_type: restockType,
        notes,
        cassettes: config
          .filter(({ bill_note_id }) => bill_note_id)
          .map(({ cassette_index, count, bill_note_id }) => ({
            notes_count: count ?? 0,
            bill_note_id,
            cassette_index,
          })),
        decommissioned_cassettes: config
          .filter(({ bill_note_id }) => !bill_note_id)
          .map(({ cassette_index }) => ({ cassette_index })),
      };
      await editRestock({ id, data }).unwrap();
      hide();
      toast.success('Load Order updated successfully');
    } catch (error) {
      toast.error((error as any).message ?? DEFAULT_ERROR_MESSAGE);
    }
  };

  useEffect(() => {
    if (error) hide();
  }, [error]);

  useEffect(() => {
    if (!order) return;
    setNotes(order.notes ?? '');
    setRestockType(order.restock_type ?? RestockRequestType.Swap);
  }, [order]);

  if (isLoadingOrder) return <Loader />;
  return (
    <>
      <DialogContent>
        <Stack gap={4} py={3}>
          <Box>
            <Typography variant="h6" mb={1}>
              Load Order Type
            </Typography>
            <Select
              onChange={({ target: { value } }) => setRestockType(value as RestockRequestType)}
              disabled={isLoading}
              value={restockType}
              fullWidth
            >
              <MenuItem value={RestockRequestType.Swap}>Swap</MenuItem>
              <MenuItem value={RestockRequestType.Add}>Add</MenuItem>
            </Select>
          </Box>
          {order?.cassettes?.map((cassette) => (
            <CassetteConfigurationCard
              index={cassette.cassette_index ?? 0}
              key={cassette.cassette_index}
              onChange={handleChange}
              value={cassette}
            />
          ))}
          <Box>
            <Typography variant="h6" mb={2}>
              Miscellaneous Information
            </Typography>
            <FormControl fullWidth>
              <InputLabel variant="standard" size="small">
                Note
              </InputLabel>
              <TextField
                placeholder="Add any additional instructions or notes for the field technician"
                onChange={({ target }) => setNotes(target.value)}
                disabled={isLoading}
                variant="standard"
                value={notes}
                fullWidth
                multiline
              />
            </FormControl>
          </Box>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={hide}>Close</Button>
        <LoadingButton
          loading={isLoading}
          disabled={!isValid}
          onClick={handleUpdate}
          variant="contained"
        >
          Update
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default EditLoadOrder;
